<script setup lang="ts">
import {inject, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import {
    clearGroupAnsweredCounts as apiClearGroupAnsweredCounts,
    getGroups as apiGetGroups,
} from "@/Services/Admin/admin-groups";
import {Button, Column, DataTable, MultiSelect, SplitButton} from "@/Components/Library";
import {PageHeading, TableRecordActions} from "@/Components/Global";
import type dayjs from "dayjs";
import type {AdminIndex} from "@/Types";
import {FilterMatchMode} from "@primevue/core/api";
import {useConfirm} from "primevue/useconfirm";
import {useToast} from "primevue/usetoast";

type GroupLocal = any;

const currentRoute = useRoute();
const router = useRouter();
const $date = inject("date") as typeof dayjs;
const groups = ref<AdminIndex<GroupLocal>>();
const confirm = useConfirm();
const toast = useToast();

const filters = ref({
    anonymization: {
        value: [],
        matchMode: FilterMatchMode.IN,
    },
});

const getGroups = async () =>
    apiGetGroups({
        include: ["membersCount"],
        ...currentRoute.query,
    }).then((data) => (groups.value = data));

const clearAnsweredCounts = async (
    event: Event,
    groupIds: string | number | Array<string | number> | null = null,
) => {
    confirm.require({
        message: "Are you sure you want to clear answered counts cache?",
        header: "Clear Answered Counts Cache",
        target: event.target as HTMLElement,
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Yes",
        acceptClass: "bg-askr-green text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectLabel: "No",
        rejectClass: "px-2 py-1 rounded-lg hover:brightness-110",
        accept: async () => {
            await apiClearGroupAnsweredCounts(groupIds);
            toast.add({
                severity: "success",
                summary: "Success",
                detail: "Answered counts cache cleared successfully",
                life: 3000,
            });
            await getGroups();
        },
    });
};

const groupActions = [
    {
        label: "Clear All Cache",
        icon: "mdi:cached",
        command: ({originalEvent}) => clearAnsweredCounts(originalEvent),
    },
];

getGroups();
</script>
<template>
    <div>
        <div class="m-container">
            <PageHeading title="Groups">
                <SplitButton
                    rounded
                    class="ml-auto bg-askr-green text-sm text-white hover:brightness-110"
                    label="Add Group"
                    icon="prime:plus"
                    :model="groupActions"
                    @click="router.push({name: 'admin.groups.create'})"
                />
            </PageHeading>
        </div>
        <DataTable
            :data="groups"
            dataKey="id"
            :loading="false"
            :filters="filters"
            @filter="getGroups"
            defaultSort="-created_at"
        >
            <Column expander style="width: 0" />
            <Column field="id" header="ID">
                <template #body="{data}">
                    <RouterLink
                        :to="{
                            name: 'admin.groups.show',
                            params: {group: data.id},
                        }"
                        class="hover:underline"
                    >
                        {{ data.id }}
                    </RouterLink>
                </template>
            </Column>
            <Column field="name" header="Name">
                <template #body="{data}">
                    <RouterLink
                        :to="{
                            name: 'admin.groups.show',
                            params: {group: data.id},
                        }"
                        class="hover:underline"
                    >
                        {{ data.name }}
                    </RouterLink>
                </template>
            </Column>
            <Column
                field="anonymization"
                header="Anonymization"
                :sortable="true"
                :showFilterMatchModes="false"
                filter-field="anonymization"
            >
                <template #body="{data}">
                    {{ data.anonymization.name }}
                </template>
                <template #filter="{filterModel, filterCallback}">
                    <MultiSelect
                        v-model="filterModel.value"
                        @change="filterCallback"
                        :options="[
                            {
                                value: 'full_name',
                                label: 'Full Name',
                            },
                            {
                                value: 'nickname',
                                label: 'Nickname',
                            },
                            {
                                value: 'initials_only',
                                label: 'Initials Only',
                            },
                            {
                                value: 'fully_anonymous',
                                label: 'Fully Anonymous',
                            },
                        ]"
                        option-label="label"
                        option-value="value"
                        placeholder="Select anonymization types"
                        filter
                        class="p-column-filter"
                        style="min-width: 14rem"
                        :maxSelectedLabels="1"
                    />
                </template>
                <template #filterclear="{filterCallback}" />
                <template #filterapply="{filterCallback}" />
            </Column>
            <Column field="members_count" header="Members" :sortable="true" />
            <Column field="created_at" header="Created At" :sortable="true">
                <template #body="{data}">
                    {{ $date(data.created_at).format("DD/MM/YYYY HH:mm") }}
                </template>
            </Column>
            <Column field="deleted_at" header="Deleted At" :sortable="true">
                <template #body="{data}">
                    {{ data.deleted_at ? $date(data.deleted_at).format("DD/MM/YYYY HH:mm") : null }}
                </template>
            </Column>
            <Column header="Actions" style="width: 0">
                <template #body="{data}">
                    <TableRecordActions
                        :name="data.name"
                        :deleted="!!data.deleted_at"
                        :routes="{
                            show: {
                                name: 'admin.groups.show',
                                params: {group: data.id},
                            },
                            edit: {
                                name: 'admin.groups.edit',
                                params: {group: data.id},
                            },
                            delete: route('admin.groups.destroy', data.id),
                            restore: route('admin.groups.restore', data.id),
                        }"
                        @deleted="getGroups"
                    >
                        <Button
                            v-tooltip.left="`Clear answered counts cache for ${data.name}`"
                            class="h-8 w-8 bg-slate-700 text-white"
                            :icon="{
                                icon: 'mdi:cached',
                                width: 16,
                            }"
                            @click="clearAnsweredCounts($event, data.id)"
                        />
                    </TableRecordActions>
                </template>
            </Column>
            <template #expansion="{data}">
                <pre>{{ data }}</pre>
            </template>
        </DataTable>
    </div>
</template>
<style scoped></style>
